import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const COMMENTS = [
  { en: "What's the name of this film?", ja: "この映画の名前はなんですか？" },
  { en: "Tell me the drama title!", ja: "ドラマの名前を教えて！" },
  { en: "The camerawork is so unique.", ja: "カメラワークが独特ですね。" },
  { en: "The light effects are beautiful.", ja: "光の演出がすてき。" },
  { en: "I love the texture — not quite real, not quite CG.", ja: "実写とも違う質感が好き。" },
  { en: "Are there any related products?", ja: "関連商品はありますか？" },
  { en: "Is there a Kuzuneko photo book?", ja: "くずねこの写真集は？" },
  { en: "I'm a fan of the wolf man!", ja: "狼男が好きです。" },
  { en: "My hometown is in this city.", ja: "実家がこの街にあります。" },
  { en: "I find myself looking up at the sky now.", ja: "空をみあげて探してしまいます。" },
  { en: "I believe they really live in this city.", ja: "街に彼らがいると信じています。" },
  { en: "The dragon footage gave me chills.", ja: "龍の映像が鳥肌たちました。" },
  { en: "Is this actually Kawaguchi?", ja: "これ本当に川口ですか？" },
  { en: "I walked around the city looking for them.", ja: "街を歩いて探してしまいました。" },
  { en: "The anamorphic lens look is incredible.", ja: "アナモルフィックレンズの質感が最高。" },
  { en: "The cat-eared woman is real, right?", ja: "猫耳の女性は本物ですよね？" },
  { en: "Where did you shoot this?", ja: "どこで撮影しましたか？" },
  { en: "The night street scenes are perfect.", ja: "夜の街の撮影が完璧です。" },
  { en: "I showed my friends and they were stunned.", ja: "友達に見せたら驚いてました。" },
  { en: "I can't believe this was made with AI.", ja: "AIで作ったとは信じられない。" },
  { en: "Are there more episodes coming?", ja: "続きはありますか？" },
  { en: "The squid woman had me speechless.", ja: "イカ女には言葉を失いました。" },
  { en: "Keep making more of this world!", ja: "この世界をもっと作り続けてください！" },
  { en: "The cinematic quality is extraordinary.", ja: "映像のクオリティが驚異的です。" },
  { en: "I love how the shadows move.", ja: "影の動きが好きです。" },
  { en: "Found you on social — instantly followed.", ja: "SNSで見つけて即フォローしました。" },
  { en: "The tiger squad raid was intense!", ja: "虎特殊部隊の急襲シーンは迫力満点！" },
  { en: "The museum scenes feel so real.", ja: "博物館のシーンがリアルすぎる。" },
  { en: "The sound design is also incredible.", ja: "音響演出もすごいですね。" },
  { en: "This deserves a full series.", ja: "これは連続ドラマにすべきです。" },
];

const EXIT_VARIANTS: Record<string, object> = {
  fadeUp: { opacity: 0, y: -28, transition: { duration: 0.55 } },
  fadeDown: { opacity: 0, y: 28, transition: { duration: 0.55 } },
  dissolve: { opacity: 0, scale: 0.88, transition: { duration: 0.6 } },
  slideLeft: { opacity: 0, x: -40, transition: { duration: 0.5 } },
  slideRight: { opacity: 0, x: 40, transition: { duration: 0.5 } },
  blur: { opacity: 0, filter: "blur(8px)", transition: { duration: 0.65 } },
};
const EFFECT_NAMES = ["fadeUp", "fadeDown", "dissolve", "slideLeft", "slideRight", "blur"];

let _idCounter = 0;

interface CommentItem {
  id: number;
  comment: { en: string; ja: string };
  topPct: number;
  side: "left" | "right";
  edgePct: number;
  effect: string;
}

export function FanComments({ containerRef }: { containerRef?: React.RefObject<HTMLDivElement> }) {
  const [items, setItems] = useState<CommentItem[]>([]);
  const seen = useRef(new Set<number>());
  const fallbackRef = useRef<HTMLDivElement>(null);
  const ref = containerRef ?? fallbackRef;

  const addComment = useCallback(() => {
    setItems((prev) => {
      if (prev.length >= 5) return prev;
      let idx = Math.floor(Math.random() * COMMENTS.length);
      let tries = 0;
      while (seen.current.has(idx) && tries < 20) {
        idx = Math.floor(Math.random() * COMMENTS.length);
        tries++;
      }
      if (seen.current.has(idx)) return prev;
      seen.current.add(idx);
      if (seen.current.size > COMMENTS.length * 0.7) seen.current.clear();
      const id = ++_idCounter;
      const side: "left" | "right" = id % 2 === 0 ? "left" : "right";
      const topPct = 10 + Math.random() * 75;
      const edgePct = 8 + Math.random() * 20;
      const effect = EFFECT_NAMES[Math.floor(Math.random() * EFFECT_NAMES.length)];
      return [...prev, { id, comment: COMMENTS[idx], topPct, side, edgePct, effect }];
    });
  }, []);

  const removeComment = useCallback((id: number) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  useEffect(() => {
    const timer = setInterval(() => addComment(), 1600);
    return () => clearInterval(timer);
  }, [addComment]);

  useEffect(() => {
    if (items.length === 0) return;
    const last = items[items.length - 1];
    const t = setTimeout(() => removeComment(last.id), 4200);
    return () => clearTimeout(t);
  }, [items, removeComment]);

  return (
    <div
      ref={ref}
      style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}
    >
      <AnimatePresence>
        {items.map((item) => {
          const exitVariant = EXIT_VARIANTS[item.effect];
          const posStyle =
            item.side === "left"
              ? { left: `${item.edgePct}%`, right: "auto" }
              : { right: `${item.edgePct}%`, left: "auto" };
          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 0.82, y: 0, scale: 1 }}
              exit={exitVariant as any}
              transition={{ duration: 0.4 }}
              style={{
                position: "absolute",
                top: `${item.topPct}%`,
                ...posStyle,
                maxWidth: "clamp(160px, 28vw, 280px)",
                zIndex: 20,
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  background: "rgba(8,8,20,0.72)",
                  border: "1px solid rgba(100,180,255,0.18)",
                  borderRadius: 12,
                  padding: "8px 14px",
                  backdropFilter: "blur(8px)",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
                }}
              >
                <p style={{ color: "rgba(255,255,255,0.92)", fontSize: "0.78rem", lineHeight: 1.4, marginBottom: 2 }}>
                  {item.comment.en}
                </p>
                <p style={{ color: "rgba(180,220,255,0.55)", fontSize: "0.65rem", lineHeight: 1.3 }}>
                  {item.comment.ja}
                </p>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
